/**
 * Phase 2L-A47 — Stage 1 inbound email triage UI.
 *
 * Admin-only review queue for inbound customer replies that the
 * auto-router couldn't confidently link to a booking. The actions
 * here are deliberate (never automatic): admins can mark a parked
 * row as Reviewed, Ignored, or manually Linked to an existing
 * booking reference.
 *
 * Notes:
 *   • Body shown here is only the ~500-char snippet the backend
 *     stored — we never persist the raw body or attachment bytes.
 *   • Linking creates an `incoming` booking_messages row tagged
 *     `match_method="manual_admin"` so it appears in the booking
 *     timeline alongside auto-matched replies.
 *   • Lab techs cannot reach this page — both the API and the route
 *     are admin-only.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Mail,
  MailQuestion,
  CheckCircle2,
  EyeOff,
  Link2,
  ShieldAlert,
  Paperclip,
  Inbox,
  X,
  History,
  RefreshCw,
} from "lucide-react";
import { PortalShell } from "@/components/PortalShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import api, { formatApiError } from "@/lib/api";
import { formatDateTimeAU } from "@/lib/formatDateAU";
import { toast } from "sonner";

// Nov 2026 — approved one-time historical recovery for the 11 customer
// replies missed during the Resend inbound webhook outage (24 Oct – 30
// Oct 2026). Parameters agreed in writing with the business owner:
// suppress_alerts (no retroactive staff "new reply!" pings for weeks-old
// conversations) + per-booking resolution_notes explaining why each one
// needs no further action. Kept as a fixed constant (not admin-editable)
// so this sensitive one-off action can't be accidentally run with
// different parameters than what was approved.
const HISTORICAL_RECOVERY_PAYLOAD = {
  confirm: true,
  suppress_alerts: true,
  resolution_notes: {
    "SCWT-584608": "Report corrections already handled.",
    "SCWT-A0240C": "Report corrections already handled.",
    "SCWT-60FDE5": "Old booking cancelled; replacement booking created and paid. No further payment follow-up needed.",
    "SCWT-C58BE8": "Customer confirmed test kit received.",
    "SCWT-9A9232": "Booking archived following cancellation request.",
    "SCWT-CFCC22": "Booking archived. Customer requested no further contact.",
    "SCWT-84AEED": "Customer contacted; arranging filtration elsewhere.",
    "SCWT-6BA7DA": "Marika left a voicemail; an earlier staff response was also recorded.",
  },
  recover_attachment_ids: {
    "6f351a62-555a-48b4-a875-beb8d8b5da2e": ["8f0aa0c2-8e0e-4cc5-89e4-dff2f3cc3658"],
  },
};

const STATUS_FILTERS = [
  { key: "needs_review", label: "Needs review", tone: "bg-amber-50 text-amber-800" },
  { key: "reviewed",     label: "Reviewed",     tone: "bg-sky-50 text-sky-800" },
  { key: "linked",       label: "Linked",       tone: "bg-emerald-50 text-emerald-800" },
  { key: "ignored",      label: "Ignored",      tone: "bg-slate-100 text-slate-600" },
  { key: "all",          label: "All",          tone: "bg-slate-900 text-white" },
];

const REASON_LABEL = {
  no_match:        "No booking match",
  fetch_failed:    "Provider fetch failed",
  signature_failed: "Signature check failed",
};

export default function AdminUnmatchedEmails() {
  const [filter, setFilter] = useState("needs_review");
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);  // id currently being acted on
  const [drawer, setDrawer] = useState(null);  // selected item for detail panel
  const [linkRef, setLinkRef] = useState("");

  // Historical recovery (Nov 2026 webhook outage) ------------------------
  const [recoveryPreview, setRecoveryPreview] = useState(null);
  const [recoveryLoading, setRecoveryLoading] = useState(true);
  const [confirmExecuteOpen, setConfirmExecuteOpen] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [recoveryResult, setRecoveryResult] = useState(null);

  const loadRecoveryPreview = async () => {
    setRecoveryLoading(true);
    try {
      const { data } = await api.get("/admin/inbound-email/recovery/dry-run?limit=100");
      setRecoveryPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load recovery preview");
    } finally {
      setRecoveryLoading(false);
    }
  };

  useEffect(() => { loadRecoveryPreview(); }, []); // eslint-disable-line

  const runExecute = async () => {
    setExecuting(true);
    try {
      const { data } = await api.post(
        "/admin/inbound-email/recovery/execute",
        HISTORICAL_RECOVERY_PAYLOAD,
      );
      setRecoveryResult(data);
      setConfirmExecuteOpen(false);
      toast.success(
        `Recovered ${data.processed} message${data.processed === 1 ? "" : "s"} — no customer or staff emails sent.`,
      );
      await loadRecoveryPreview();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Recovery failed");
    } finally {
      setExecuting(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/admin/inbound-email/unmatched?status=${encodeURIComponent(filter)}`,
      );
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [filter]); // eslint-disable-line

  const callAction = async (id, action, body) => {
    setActing(id);
    try {
      const { data } = await api.patch(
        `/admin/inbound-email/unmatched/${id}/${action}`,
        body || {},
      );
      const updated = data.item;
      // Optimistically replace in the local list (or drop if it left the filter)
      setItems((curr) => {
        if (filter === "all" || (updated && updated.status === filter)) {
          return curr.map((r) => (r.id === id ? updated : r));
        }
        return curr.filter((r) => r.id !== id);
      });
      // Refresh counts for the pills.
      await load();
      if (drawer && drawer.id === id) {
        setDrawer(updated);
        if (action === "link") setLinkRef("");
      }
      return data;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Action failed");
      throw e;
    } finally {
      setActing(null);
    }
  };

  const onReview = (row) => callAction(row.id, "review")
    .then(() => toast.success("Marked as reviewed"));

  const onIgnore = (row) => {
    if (!window.confirm("Ignore this email? It stays in the audit log but disappears from the queue.")) return;
    callAction(row.id, "ignore", {}).then(() => toast.success("Marked as ignored"));
  };

  const onLink = (row, ref) => {
    const reference = (ref || "").trim();
    if (!reference) {
      toast.error("Enter a booking reference (e.g. SCWT-ABC123)");
      return;
    }
    callAction(row.id, "link", { reference })
      .then((data) => {
        toast.success(`Linked to ${data.reference}`);
      })
      .catch(() => { /* toast already shown */ });
  };

  const totalCount = counts.all || 0;
  const needsReviewCount = counts.needs_review || 0;

  const allAlreadyRecovered = recoveryPreview && recoveryPreview.total > 0
    && recoveryPreview.already_present === recoveryPreview.total;

  return (
    <PortalShell title="Unmatched inbound emails" subtitle="Admin · Triage">
      {/* Historical recovery — Nov 2026 webhook outage */}
      <div
        data-testid="historical-recovery-panel"
        className={`rounded-3xl border p-6 mb-6 ${
          allAlreadyRecovered
            ? "border-emerald-200 bg-emerald-50"
            : "border-indigo-200 bg-indigo-50"
        }`}
      >
        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 w-10 h-10 rounded-full inline-flex items-center justify-center ${
            allAlreadyRecovered ? "bg-emerald-100" : "bg-indigo-100"
          }`}>
            <History className={`w-5 h-5 ${allAlreadyRecovered ? "text-emerald-700" : "text-indigo-700"}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className={`text-base font-bold ${allAlreadyRecovered ? "text-emerald-900" : "text-indigo-900"}`}>
              Historical recovery — Oct 2026 webhook outage
            </h2>
            <p className={`text-sm mt-1 leading-relaxed ${allAlreadyRecovered ? "text-emerald-800" : "text-indigo-800"}`}>
              Restores the customer replies that arrived while the inbound webhook was pointed at the wrong URL into
              their booking timelines — with <code className="bg-white/60 px-1 rounded">suppress_alerts</code> on, so
              no historical &quot;new reply!&quot; emails go to staff, and Marika&apos;s per-booking notes attached.
              Never sends a customer email, never reopens a booking.
            </p>

            {recoveryLoading ? (
              <div className="text-sm text-slate-500 mt-3">Loading preview…</div>
            ) : recoveryPreview ? (
              <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="historical-recovery-counts">
                <Stat label="Total" value={recoveryPreview.total} />
                <Stat label="Already recovered" value={recoveryPreview.already_present} />
                <Stat label="Would process" value={recoveryPreview.would_process} />
              </div>
            ) : null}

            {!recoveryLoading && recoveryPreview && recoveryPreview.events?.length > 0 && (
              <ul className="mt-3 space-y-1 text-xs text-slate-700 max-h-40 overflow-y-auto" data-testid="historical-recovery-events">
                {recoveryPreview.events.map((ev) => (
                  <li key={ev.provider_email_id} className="flex items-center gap-2">
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                      ev.outcome === "already_present_skip" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                    }`}>
                      {ev.outcome === "already_present_skip" ? "recovered" : "pending"}
                    </span>
                    <span className="text-slate-500">{formatDateTimeAU(ev.received_at)}</span>
                    <span className="truncate">{(ev.to || []).join(", ")}</span>
                  </li>
                ))}
              </ul>
            )}

            <div className="flex flex-wrap items-center gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={recoveryLoading}
                onClick={loadRecoveryPreview}
                data-testid="historical-recovery-refresh-btn"
                className="h-8 rounded-full text-xs"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh preview
              </Button>
              {!allAlreadyRecovered && (
                <Button
                  size="sm"
                  disabled={recoveryLoading || !recoveryPreview?.would_process}
                  onClick={() => setConfirmExecuteOpen(true)}
                  data-testid="historical-recovery-execute-btn"
                  className="h-8 rounded-full text-xs bg-indigo-600 hover:bg-indigo-500 text-white"
                >
                  Execute historical recovery ({recoveryPreview?.would_process ?? 0})
                </Button>
              )}
              {allAlreadyRecovered && (
                <span className="text-xs font-semibold text-emerald-800" data-testid="historical-recovery-done">
                  All {recoveryPreview.total} messages already recovered.
                </span>
              )}
            </div>

            {recoveryResult && (
              <div className="mt-3 rounded-xl border border-slate-200 bg-white p-3 text-xs text-slate-700" data-testid="historical-recovery-result">
                Processed <strong>{recoveryResult.processed}</strong>, already-present <strong>{recoveryResult.already_present_skipped}</strong>,
                parked-unmatched <strong>{recoveryResult.parked_unmatched}</strong>, alerts suppressed by request:{" "}
                <strong>{(recoveryResult.alerts_suppressed_by_request || []).length}</strong>,
                alerts actually sent: <strong>{(recoveryResult.alerts_sent_for || []).length}</strong>.
              </div>
            )}
          </div>
        </div>
      </div>

      <AlertDialog open={confirmExecuteOpen} onOpenChange={(open) => !executing && setConfirmExecuteOpen(open)}>
        <AlertDialogContent data-testid="confirm-historical-recovery">
          <AlertDialogHeader>
            <AlertDialogTitle>Execute historical recovery?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                This recovers {recoveryPreview?.would_process ?? 0} customer repl{recoveryPreview?.would_process === 1 ? "y" : "ies"} into their
                booking timelines, with staff alerts suppressed and Marika&apos;s resolution notes attached. It is idempotent —
                already-recovered messages are skipped — and never sends a customer or staff email, never reopens a booking,
                and never changes a payment record.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="confirm-historical-recovery-cancel" disabled={executing}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-historical-recovery-confirm"
              onClick={(e) => { e.preventDefault(); runExecute(); }}
              disabled={executing}
              className="bg-indigo-600 hover:bg-indigo-500"
            >
              {executing ? "Recovering…" : "Execute recovery"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Intro / context */}
      <div
        data-testid="unmatched-intro"
        className="rounded-3xl border border-amber-200 bg-amber-50 p-6 mb-6 flex flex-col sm:flex-row sm:items-start gap-4"
      >
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 inline-flex items-center justify-center">
          <ShieldAlert className="w-5 h-5 text-amber-700" />
        </div>
        <div className="flex-1">
          <h2 className="text-base font-bold text-amber-900">Customer replies that didn&apos;t auto-route</h2>
          <p className="text-sm text-amber-800 mt-1 leading-relaxed">
            When an inbound email lacks our reply-to token or a recognisable <code className="bg-amber-100 px-1 rounded">[SCWT-…]</code> subject, it lands here for manual triage. Review, ignore, or link it to an existing booking — we never store attachment bytes or the full body.
          </p>
          {needsReviewCount > 0 && (
            <p className="text-xs text-amber-900 font-semibold mt-2">
              {needsReviewCount} {needsReviewCount === 1 ? "email needs" : "emails need"} review.
            </p>
          )}
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="unmatched-filter-bar">
        {STATUS_FILTERS.map((f) => {
          const active = filter === f.key;
          const count = f.key === "all" ? totalCount : (counts[f.key] || 0);
          return (
            <button
              key={f.key}
              data-testid={`unmatched-filter-${f.key}`}
              onClick={() => setFilter(f.key)}
              className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                active ? "bg-slate-900 text-white" : `${f.tone} hover:opacity-80`
              }`}
            >
              {f.label}
              <span className={`px-1.5 py-0.5 rounded text-[10px] ${active ? "bg-white/20" : "bg-white/60"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Table */}
      <div
        data-testid="unmatched-table"
        className="rounded-3xl bg-white border border-slate-200 overflow-hidden"
      >
        {loading ? (
          <div className="p-10 text-center text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <EmptyState filter={filter} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Received</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">From</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Subject</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Snippet</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Detected ref</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Reason</th>
                  <th className="text-left p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Status</th>
                  <th className="text-right p-4 text-[11px] uppercase tracking-wider font-bold text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <UnmatchedRow
                    key={row.id}
                    row={row}
                    busy={acting === row.id}
                    onReview={onReview}
                    onIgnore={onIgnore}
                    onLink={onLink}
                    onOpen={() => { setDrawer(row); setLinkRef(row.detected_reference || ""); }}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail drawer */}
      {drawer && (
        <DetailDrawer
          row={drawer}
          linkRef={linkRef}
          setLinkRef={setLinkRef}
          onClose={() => setDrawer(null)}
          onReview={onReview}
          onIgnore={onIgnore}
          onLink={onLink}
          busy={acting === drawer.id}
        />
      )}
    </PortalShell>
  );
}

function Stat({ label, value }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/70 text-xs font-semibold text-slate-700">
      {label}: <span className="text-slate-900">{value}</span>
    </span>
  );
}

function EmptyState({ filter }) {
  return (
    <div className="p-12 text-center text-slate-500">
      <Inbox className="w-10 h-10 text-slate-300 mx-auto mb-3" />
      <div className="text-sm font-semibold text-slate-700">
        {filter === "needs_review" ? "Inbox zero — nothing to triage." : "Nothing to show in this view."}
      </div>
      <div className="text-xs text-slate-500 mt-1">
        Unmatched inbound replies will appear here once Resend inbound is live.
      </div>
    </div>
  );
}

function UnmatchedRow({ row, busy, onReview, onIgnore, onLink, onOpen }) {
  const status = row.status || "needs_review";
  const isClosed = status === "linked" || status === "ignored";
  const [showLink, setShowLink] = useState(false);
  const [refInput, setRefInput] = useState(row.detected_reference || "");

  const snippet = row.body_text_snippet || "(no body captured)";
  const attachments = row.attachments_meta || [];

  return (
    <tr
      data-testid={`unmatched-row-${row.id}`}
      className="border-t border-slate-100 hover:bg-slate-50/60 align-top"
    >
      <td className="p-4 text-xs text-slate-500 whitespace-nowrap">
        {formatDateTimeAU(row.received_at)}
      </td>
      <td className="p-4 text-slate-900 max-w-[200px] truncate" title={row.from_email}>
        {row.from_email || "—"}
      </td>
      <td className="p-4 text-slate-700 max-w-[240px] truncate" title={row.subject || ""}>
        <button onClick={onOpen} className="text-left hover:underline" data-testid={`unmatched-open-${row.id}`}>
          {row.subject || "(no subject)"}
        </button>
        {attachments.length > 0 && (
          <span className="inline-flex items-center gap-1 ml-2 text-[10px] text-slate-500">
            <Paperclip className="w-3 h-3" /> {attachments.length}
          </span>
        )}
      </td>
      <td className="p-4 text-xs text-slate-600 max-w-[260px]">
        <div className="line-clamp-2">{snippet}</div>
      </td>
      <td className="p-4 text-xs whitespace-nowrap">
        {row.detected_reference ? (
          <code className="bg-slate-100 px-2 py-0.5 rounded text-slate-700">{row.detected_reference}</code>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </td>
      <td className="p-4 text-xs">
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 font-medium">
          {REASON_LABEL[row.reason] || row.reason || "unknown"}
        </span>
      </td>
      <td className="p-4">
        <StatusPill status={status} />
        {status === "linked" && row.linked_reference && (
          <a
            href={`/admin/bookings/${row.linked_reference}`}
            className="block text-[11px] mt-1 text-cyan-700 hover:underline"
          >
            → {row.linked_reference}
          </a>
        )}
      </td>
      <td className="p-4">
        <div className="flex flex-col items-end gap-2">
          {!isClosed && (
            <div className="flex gap-1.5">
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => onReview(row)}
                data-testid={`unmatched-review-btn-${row.id}`}
                className="h-7 px-2.5 text-[11px] rounded-full"
                title="Mark as reviewed"
              >
                <CheckCircle2 className="w-3 h-3 mr-1" /> Review
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={busy}
                onClick={() => onIgnore(row)}
                data-testid={`unmatched-ignore-btn-${row.id}`}
                className="h-7 px-2.5 text-[11px] rounded-full"
                title="Ignore (spam/auto-reply)"
              >
                <EyeOff className="w-3 h-3 mr-1" /> Ignore
              </Button>
              <Button
                size="sm"
                disabled={busy}
                onClick={() => setShowLink((v) => !v)}
                data-testid={`unmatched-link-toggle-${row.id}`}
                className="h-7 px-2.5 text-[11px] rounded-full bg-cyan-600 hover:bg-cyan-500 text-white"
                title="Link to booking"
              >
                <Link2 className="w-3 h-3 mr-1" /> Link
              </Button>
            </div>
          )}
          {showLink && !isClosed && (
            <div className="flex items-center gap-1.5">
              <Input
                value={refInput}
                onChange={(e) => setRefInput(e.target.value.toUpperCase())}
                placeholder="SCWT-ABC123"
                data-testid={`unmatched-link-input-${row.id}`}
                className="h-7 w-32 text-[11px] rounded-lg uppercase font-mono"
                autoFocus
              />
              <Button
                size="sm"
                disabled={busy || !refInput}
                onClick={() => onLink(row, refInput)}
                data-testid={`unmatched-link-submit-${row.id}`}
                className="h-7 px-2.5 text-[11px] rounded-full bg-emerald-600 hover:bg-emerald-500 text-white"
              >
                Link →
              </Button>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}

function StatusPill({ status }) {
  const tones = {
    needs_review: "bg-amber-50 text-amber-800",
    reviewed:     "bg-sky-50 text-sky-800",
    linked:       "bg-emerald-50 text-emerald-800",
    ignored:      "bg-slate-100 text-slate-600",
  };
  const labels = {
    needs_review: "Needs review",
    reviewed:     "Reviewed",
    linked:       "Linked",
    ignored:      "Ignored",
  };
  return (
    <span
      data-testid={`unmatched-status-${status}`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${tones[status] || "bg-slate-100 text-slate-700"}`}
    >
      {labels[status] || status}
    </span>
  );
}

function DetailDrawer({ row, linkRef, setLinkRef, onClose, onReview, onIgnore, onLink, busy }) {
  const status = row.status || "needs_review";
  const isClosed = status === "linked" || status === "ignored";
  const attachments = row.attachments_meta || [];

  // Close on Escape
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const auditRows = useMemo(() => {
    const rows = [];
    if (row.reviewed_at) rows.push({ label: "Reviewed", at: row.reviewed_at, by: row.reviewed_by });
    if (row.ignored_at)  rows.push({ label: "Ignored",  at: row.ignored_at,  by: row.ignored_by });
    if (row.linked_at)   rows.push({ label: `Linked to ${row.linked_reference}`, at: row.linked_at, by: row.linked_by });
    return rows;
  }, [row]);

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" data-testid="unmatched-drawer">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm"
      />
      <aside className="absolute right-0 top-0 bottom-0 w-full sm:w-[480px] bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <MailQuestion className="w-4 h-4 text-amber-700" />
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500">Inbound · Triage</div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100"
            aria-label="Close drawer"
            data-testid="unmatched-drawer-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <div>
            <h2 className="font-display text-xl font-bold text-slate-900 break-words" data-testid="unmatched-drawer-subject">
              {row.subject || "(no subject)"}
            </h2>
            <div className="text-xs text-slate-500 mt-1">
              <Mail className="inline w-3.5 h-3.5 mr-1 -mt-0.5" />
              From <span className="font-semibold text-slate-700">{row.from_email || "—"}</span>
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              Received {formatDateTimeAU(row.received_at)}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              <StatusPill status={status} />
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 text-[11px] font-medium">
                {REASON_LABEL[row.reason] || row.reason || "unknown"}
              </span>
              {row.detected_reference && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-medium">
                  Detected: <code>{row.detected_reference}</code>
                </span>
              )}
            </div>
          </div>

          {row.linked_reference && (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
              <div className="text-[11px] uppercase tracking-wider font-bold text-emerald-700 mb-1">Linked booking</div>
              <a
                href={`/admin/bookings/${row.linked_reference}`}
                className="text-sm font-mono font-bold text-emerald-900 hover:underline"
                data-testid="unmatched-drawer-linked-ref"
              >
                {row.linked_reference}
              </a>
            </div>
          )}

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3.5">
            <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Body snippet (≤500 chars)</div>
            <p className="text-sm text-slate-800 whitespace-pre-wrap leading-relaxed" data-testid="unmatched-drawer-snippet">
              {row.body_text_snippet || "(no body captured)"}
            </p>
          </div>

          {attachments.length > 0 && (
            <div className="rounded-xl border border-slate-200 p-3.5">
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5 inline-flex items-center gap-1">
                <Paperclip className="w-3 h-3" /> Attachments (metadata only)
              </div>
              <ul className="text-xs text-slate-700 space-y-1">
                {attachments.map((a, i) => (
                  <li key={i} className="flex justify-between gap-2">
                    <span className="font-mono truncate">{a.filename || "(unnamed)"}</span>
                    <span className="text-slate-500 flex-shrink-0">{a.content_type || ""} · {a.size != null ? `${a.size}b` : ""}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {auditRows.length > 0 && (
            <div className="rounded-xl border border-slate-200 p-3.5">
              <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Audit trail</div>
              <ul className="text-xs text-slate-700 space-y-1">
                {auditRows.map((r, i) => (
                  <li key={i}>
                    <span className="font-semibold">{r.label}</span> by <span className="font-mono">{r.by || "—"}</span>
                    <span className="text-slate-500"> · {formatDateTimeAU(r.at)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!isClosed && (
            <div className="rounded-xl border border-cyan-200 bg-cyan-50 p-3.5">
              <div className="text-[11px] uppercase tracking-wider font-bold text-cyan-800 mb-2 inline-flex items-center gap-1">
                <Link2 className="w-3.5 h-3.5" /> Link to a booking
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  value={linkRef}
                  onChange={(e) => setLinkRef(e.target.value.toUpperCase())}
                  placeholder="SCWT-ABC123"
                  data-testid="unmatched-drawer-link-input"
                  className="flex-1 h-10 rounded-lg uppercase font-mono"
                />
                <Button
                  disabled={busy || !linkRef}
                  onClick={() => onLink(row, linkRef)}
                  data-testid="unmatched-drawer-link-submit"
                  className="h-10 px-4 rounded-full bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
                >
                  Link to booking
                </Button>
              </div>
              <p className="text-[11px] text-cyan-900/80 mt-2">
                Creates an <code>incoming</code> message on the booking tagged <code>manual_admin</code>.
              </p>
            </div>
          )}
        </div>

        {!isClosed && (
          <div className="border-t border-slate-200 p-4 flex justify-end gap-2">
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => onIgnore(row)}
              data-testid="unmatched-drawer-ignore-btn"
              className="rounded-full"
            >
              <EyeOff className="w-3.5 h-3.5 mr-1.5" /> Ignore
            </Button>
            <Button
              variant="outline"
              disabled={busy}
              onClick={() => onReview(row)}
              data-testid="unmatched-drawer-review-btn"
              className="rounded-full"
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Mark reviewed
            </Button>
          </div>
        )}
      </aside>
    </div>
  );
}
